const he = require("he");
const checksum = require("checksum");

const CONTENT_REVIEW_ITEM_TEXT_FIELDS = [
	"label1",
	"label1Audio",
	"label2",
	"label2Audio",
	"comments",
	"commentsAudio"
];

const CONTENT_REVIEW_V2_ITEM_FIELD_DEFINITIONS = [
	{ property: "label1", label: "Label 1", always: true },
	{ property: "label1Audio", label: "Label 1 Audio" },
	{ property: "label2", label: "Label 2" },
	{ property: "label2Audio", label: "Label 2 Audio" },
	{ property: "comments", label: "Comments" },
	{ property: "commentsAudio", label: "Comments Audio" }
];

const CONTENT_REVIEW_V2_FIELD_DEFINITIONS = {
	activity: [
		{ property: "name", label: "Name", always: true },
		{ property: "nameAudio", label: "Name Audio" }
	],
	list: [
		{ property: "name", label: "Name", always: true },
		{ property: "nameAudio", label: "Name Audio" }
	],
	section: [
		{ property: "name", label: "Name", always: true },
		{ property: "nameAudio", label: "Name Audio" }
	],
	itemContainer: CONTENT_REVIEW_V2_ITEM_FIELD_DEFINITIONS,
	item: CONTENT_REVIEW_V2_ITEM_FIELD_DEFINITIONS
};

const CONTENT_REVIEW_V2_BLOCK_STYLES = {
	activity: { kindLabel: "Activity", headerColor: "0F2043", accentColor: "79CEDC" },
	listHeading: { kindLabel: "List", headerColor: "2563A8", accentColor: "D9EEFF" },
	sectionHeading: { kindLabel: "Section", headerColor: "B7791F", accentColor: "FFF1C2" },
	question: { kindLabel: "Item Container", headerColor: "2F855A", accentColor: "D8F3E4" },
	item: { kindLabel: "Item", headerColor: "3B7180", accentColor: "D9F0F4" }
};
const CONTENT_REVIEW_V2_ACTIVITY_ID_PATTERN = /^[A-Za-z0-9_-]{1,128}$/;
const CONTENT_REVIEW_V2_MAX_TAG_LENGTH = 2048;
const CONTENT_REVIEW_V2_RESERVED_TAG_SEGMENTS = new Set([
	"__proto__",
	"constructor",
	"prototype"
]);

const CONTENT_REVIEW_PROPS = [
	"name",
	"nameAudio",
	"namePrint",
	"itemButtonOkLabel",
	"itemButtonBackLabel",
	"itemButtonOkVocabAlternates",
	"itemButtonBackVocabAlternates",
	"fullscreenActionVariant",
	"fullscreenActionPlacement",
	"fullscreenActionFlowWhenScrollable",
	"fullscreenSwipeNavigation",
	"fullscreenSwipeSound",
	"fullscreenLabelHideControl",
	"submitLabel",
	"submitLabelWhenComplete",
	"label",
	"labelAudio",
	"labelPrint",
	"label1",
	"label1Audio",
	"label1Print",
	"label2",
	"label2Audio",
	"label2Print",
	"comments",
	"commentsAudio",
	"commentsPrint",
	"pickerItems"
];

function isItemType(type) {
	return typeof type === "string" && type.startsWith("item");
}

function isItemContainerType(type) {
	return typeof type === "string" && type.toLowerCase() === "itemcontainer";
}

function getContentReviewString(value) {
	return typeof value === "string" ? value : "";
}

function getNormalizedContentReviewV2Value(value) {
	return (value === undefined || value === null ? "" : String(value)).replace(/\r\n?/g, "\n");
}

function getContentReviewV2HashInput(value) {
	return getNormalizedContentReviewV2Value(value).replace(/\n+$/g, "");
}

function getContentReviewV2NodeType(node, kind) {
	if (kind === "activity") {
		return "activity";
	}
	if (node && typeof node.type === "string" && node.type !== "") {
		return node.type;
	}
	if (kind === "listHeading") {
		return "list";
	}
	if (kind === "sectionHeading") {
		return "section";
	}
	return "item";
}

function getContentReviewV2NodeGuid(node, kind) {
	if (!node || typeof node !== "object") {
		return "";
	}
	if (kind === "activity") {
		return getContentReviewString(node.id || node.guid);
	}
	return getContentReviewString(node.guid || node.id);
}

function createContentReviewV2ModelError(code, message, details = {}) {
	const error = new Error(message);
	error.code = code;
	Object.keys(details).forEach((key) => {
		error[key] = details[key];
	});
	return error;
}

function isValidContentReviewV2TagSegment(value) {
	if (
		typeof value !== "string" ||
		value === "" ||
		CONTENT_REVIEW_V2_RESERVED_TAG_SEGMENTS.has(value)
	) {
		return false;
	}
	for (let index = 0; index < value.length; index += 1) {
		const code = value.charCodeAt(index);
		if (code <= 31 || code === 127) {
			return false;
		}
	}
	return true;
}

function validateContentReviewV2ModelIdentity(activity) {
	const activityId = getContentReviewV2NodeGuid(activity, "activity");
	if (!CONTENT_REVIEW_V2_ACTIVITY_ID_PATTERN.test(activityId)) {
		throw createContentReviewV2ModelError(
			"CONTENT_REVIEW_V2_INVALID_ACTIVITY_ID",
			"The activity must have a safe, stable identity before it can be exported for Word review.",
			{ activityId }
		);
	}

	const seenGuids = new Set([activityId]);
	function visit(nodes) {
		if (!Array.isArray(nodes)) {
			return;
		}
		nodes.forEach((node) => {
			if (!node || typeof node !== "object" || Array.isArray(node)) {
				return;
			}
			const emittedNode = node.type === "list" || node.type === "section" || isItemType(node.type);
			if (emittedNode) {
				const guid = getContentReviewV2NodeGuid(node, "item");
				if (!isValidContentReviewV2TagSegment(guid)) {
					throw createContentReviewV2ModelError(
						"CONTENT_REVIEW_V2_MISSING_GUID",
						`Content node ${node.type || "unknown"} is missing a safe GUID.`,
						{ type: node.type, guid }
					);
				}
				if (seenGuids.has(guid)) {
					throw createContentReviewV2ModelError(
						"CONTENT_REVIEW_V2_DUPLICATE_GUID",
						`Content GUID ${guid} occurs more than once and cannot be round-tripped safely.`,
						{ guid }
					);
				}
				seenGuids.add(guid);
				if (!isValidContentReviewV2TagSegment(getContentReviewV2NodeType(node, "item"))) {
					throw createContentReviewV2ModelError(
						"CONTENT_REVIEW_V2_INVALID_TYPE",
						"Content node type cannot be represented safely in a Word review tag.",
						{ guid, type: node.type }
					);
				}
			}
			visit(node.children);
		});
	}
	visit(activity && activity.children);
}

function getContentReviewV2FieldDefinitions(type, kind) {
	if (kind === "activity") {
		return CONTENT_REVIEW_V2_FIELD_DEFINITIONS.activity;
	}
	if (kind === "listHeading") {
		return CONTENT_REVIEW_V2_FIELD_DEFINITIONS.list;
	}
	if (kind === "sectionHeading") {
		return CONTENT_REVIEW_V2_FIELD_DEFINITIONS.section;
	}
	if (isItemContainerType(type)) {
		return CONTENT_REVIEW_V2_FIELD_DEFINITIONS.itemContainer;
	}
	return CONTENT_REVIEW_V2_FIELD_DEFINITIONS.item;
}

function buildContentReviewV2Field(type, guid, property, label, value, depth) {
	const normalizedValue = getNormalizedContentReviewV2Value(value);
	const originalHash = checksum(getContentReviewV2HashInput(normalizedValue));
	const tag = [
		"ambifi",
		"v2",
		"field",
		encodeURIComponent(type),
		encodeURIComponent(guid),
		encodeURIComponent(property),
		originalHash
	].join(":");
	if (tag.length > CONTENT_REVIEW_V2_MAX_TAG_LENGTH) {
		throw createContentReviewV2ModelError(
			"CONTENT_REVIEW_V2_TAG_TOO_LONG",
			`Content field ${guid}.${property} cannot be represented safely in a Word review tag.`,
			{ guid, property, tagLength: tag.length }
		);
	}

	return {
		tag,
		scope: `${type}.${guid}.${property}`,
		guid,
		type,
		property,
		label,
		displayLabel: label,
		value: normalizedValue,
		originalHash,
		depth
	};
}

function buildContentReviewV2Fields(node, kind, depth) {
	const type = getContentReviewV2NodeType(node, kind);
	const guid = getContentReviewV2NodeGuid(node, kind);
	const fieldDefinitions = getContentReviewV2FieldDefinitions(type, kind);

	const fields = fieldDefinitions.reduce((result, fieldDefinition) => {
		const value = getNormalizedContentReviewV2Value(node && node[fieldDefinition.property]);
		if (!fieldDefinition.always && value === "") {
			return result;
		}

		result.push(buildContentReviewV2Field(
			type,
			guid,
			fieldDefinition.property,
			fieldDefinition.label,
			value,
			depth
		));
		return result;
	}, []);

	if (isItemType(type) && node && Array.isArray(node.pickerItems)) {
		node.pickerItems.forEach((pickerItem, index) => {
			if (!pickerItem || typeof pickerItem.label !== "string") {
				return;
			}
			const property = `pickerItems.[${index}].label`;
			fields.push(buildContentReviewV2Field(
				type,
				guid,
				property,
				`Picker Item ${index + 1} Label`,
				pickerItem.label,
				depth + 1
			));
		});
	}

	return fields;
}

function getContentReviewV2Title(node, kind) {
	if (kind === "activity" || kind === "listHeading" || kind === "sectionHeading") {
		return getContentReviewString(node && node.name);
	}
	return getContentReviewString(node && (node.label1 || node.navTitle));
}

function getContentReviewV2ImageSource(value) {
	return typeof value === "string" ? value.trim() : "";
}

function getContentReviewV2HeroImage(activity) {
	const heroImage = getContentReviewV2ImageSource(activity && activity.heroImage);
	return heroImage || getContentReviewV2ImageSource(activity && activity.heroImageUrl);
}

function buildContentReviewV2Image(node, kind, depth) {
	const normalizedSrc = getContentReviewV2ImageSource(node && node.image);
	if (normalizedSrc === "") {
		return null;
	}

	const title = getContentReviewV2Title(node, kind);
	return {
		src: normalizedSrc,
		guid: getContentReviewV2NodeGuid(node, kind),
		type: getContentReviewV2NodeType(node, kind),
		title,
		displayLabel: `${depth > 0 ? "\u21b3 ".repeat(Math.min(depth, 3)) : ""}${title}`,
		depth
	};
}

function buildContentReviewV2Block(node, kind, contextPath, depth = 0) {
	const type = getContentReviewV2NodeType(node, kind);
	const blockStyle = CONTENT_REVIEW_V2_BLOCK_STYLES[kind] || CONTENT_REVIEW_V2_BLOCK_STYLES.item;
	const image = buildContentReviewV2Image(node, kind, depth);
	const block = {
		kind,
		...blockStyle,
		isOutline: kind === "activity" || kind === "listHeading" || kind === "sectionHeading",
		guid: getContentReviewV2NodeGuid(node, kind),
		type,
		title: getContentReviewV2Title(node, kind),
		contextPath: contextPath.length > 0
			? contextPath.join(" \u203a ")
			: (kind === "activity" ? "Activity details" : "Top-level content"),
		fields: buildContentReviewV2Fields(node, kind, depth),
		images: image ? [image] : []
	};

	return block;
}

function appendContentReviewV2CardContent(node, fields, images, depth) {
	if (!node || typeof node !== "object" || Array.isArray(node)) {
		return;
	}

	fields.push(...buildContentReviewV2Fields(node, "item", depth));
	const image = buildContentReviewV2Image(node, "item", depth);
	if (image) {
		images.push(image);
	}
	if (Array.isArray(node.children)) {
		node.children.forEach((child) => {
			appendContentReviewV2CardContent(child, fields, images, depth + 1);
		});
	}
}

/**
 * Build the ordered view model used by the v2 DOCX template.
 *
 * Structural headings remain individual blocks while an outer item container
 * and all of its descendants become one content card. Stable source GUIDs
 * remain attached to every editable field for tagged DOCX round-trip import.
 */
export function buildContentReviewV2Model(activity) {
	const sourceActivity = activity && typeof activity === "object" && !Array.isArray(activity)
		? activity
		: {};
	validateContentReviewV2ModelIdentity(sourceActivity);
	const activityId = getContentReviewV2NodeGuid(sourceActivity, "activity");
	const parsedRevision = Number(sourceActivity.revision);
	const revision = Number.isFinite(parsedRevision) && parsedRevision >= 0
		? Math.floor(parsedRevision)
		: 0;
	const heroImage = getContentReviewV2HeroImage(sourceActivity);
	const reviewBlocks = [];

	const activityBlock = buildContentReviewV2Block(sourceActivity, "activity", []);
	if (heroImage !== "") {
		activityBlock.images = activityBlock.images.filter((image) => image.src !== heroImage);
	}
	reviewBlocks.push(activityBlock);

	function visitNodes(nodes, contextPath) {
		if (!Array.isArray(nodes)) {
			return;
		}

		nodes.forEach((node) => {
			if (!node || typeof node !== "object" || Array.isArray(node)) {
				return;
			}

			if (node.type === "list") {
				const nextContextPath = contextPath.concat(getContentReviewV2Title(node, "listHeading")).filter(Boolean);
				reviewBlocks.push(buildContentReviewV2Block(node, "listHeading", nextContextPath));
				visitNodes(node.children, nextContextPath);
				return;
			}

			if (node.type === "section") {
				const nextContextPath = contextPath.concat(getContentReviewV2Title(node, "sectionHeading")).filter(Boolean);
				reviewBlocks.push(buildContentReviewV2Block(node, "sectionHeading", nextContextPath));
				visitNodes(node.children, nextContextPath);
				return;
			}

			if (isItemType(node.type)) {
				const kind = isItemContainerType(node.type) ? "question" : "item";
				const block = buildContentReviewV2Block(node, kind, contextPath);
				block.fields = [];
				block.images = [];
				appendContentReviewV2CardContent(node, block.fields, block.images, 0);
				reviewBlocks.push(block);
				return;
			}

			visitNodes(node.children, contextPath);
		});
	}

	visitNodes(sourceActivity.children, []);

	const fieldCount = reviewBlocks.reduce((count, block) => count + block.fields.length, 0);
	return {
		reviewFormat: "ambifi-content-review",
		reviewVersion: 2,
		activityId,
		activityTitle: getContentReviewV2Title(sourceActivity, "activity"),
		heroImage,
		revision,
		fieldCount,
		documentTag: [
			"ambifi",
			"v2",
			"document",
			encodeURIComponent(activityId),
			encodeURIComponent(String(revision)),
			fieldCount
		].join(":"),
		reviewBlocks
	};
}

function normalizeContentReviewNode(node) {
	if (!node || typeof node !== "object" || Array.isArray(node)) {
		return node;
	}

	const normalizedNode = { ...node };

	if (isItemType(normalizedNode.type)) {
		CONTENT_REVIEW_ITEM_TEXT_FIELDS.forEach((field) => {
			if (typeof normalizedNode[field] !== "string") {
				normalizedNode[field] = "";
			}
		});
	}

	return normalizedNode;
}

function flattenContentReviewNodes(nodes) {
	if (!Array.isArray(nodes)) {
		return [];
	}

	return nodes.reduce((flattenedNodes, node) => {
		const normalizedNode = normalizeContentReviewNode(node);

		if (!normalizedNode || typeof normalizedNode !== "object" || Array.isArray(normalizedNode)) {
			flattenedNodes.push(normalizedNode);
			return flattenedNodes;
		}

		const children = Array.isArray(normalizedNode.children) ? normalizedNode.children : [];

		if (isItemContainerType(normalizedNode.type)) {
			delete normalizedNode.children;
			flattenedNodes.push(normalizedNode);
			flattenedNodes.push(...flattenContentReviewNodes(children));
			return flattenedNodes;
		}

		if (Array.isArray(normalizedNode.children)) {
			normalizedNode.children = flattenContentReviewNodes(children);
		}

		flattenedNodes.push(normalizedNode);
		return flattenedNodes;
	}, []);
}

/**
 * The legacy content-review DOCX templates have fixed list/section/item loops.
 * Promote item-container descendants into the template's item collection while
 * retaining the container itself so its label can still be reviewed by GUID.
 */
export function prepareActivityForContentReview(activity) {
	if (!activity || typeof activity !== "object" || Array.isArray(activity)) {
		return activity;
	}

	const preparedActivity = { ...activity };
	if (Array.isArray(preparedActivity.children)) {
		preparedActivity.children = flattenContentReviewNodes(preparedActivity.children);
	}

	return preparedActivity;
}

function encodeHtmlEntities(value) {
	return typeof value === "string" ? he.encode(value) : value;
}

export function replaceContentReviewHtmlEntities(nodes) {
	if (!Array.isArray(nodes)) {
		return;
	}

	nodes.forEach((node) => {
		if (!node || typeof node !== "object" || Array.isArray(node)) {
			return;
		}

		if (node.type === "list" || node.type === "section") {
			node.name = encodeHtmlEntities(node.name);
			node.nameAudio = encodeHtmlEntities(node.nameAudio);
		} else if (isItemType(node.type)) {
			CONTENT_REVIEW_ITEM_TEXT_FIELDS.forEach((field) => {
				node[field] = encodeHtmlEntities(node[field]);
			});
		}

		replaceContentReviewHtmlEntities(node.children);
	});
}

function addContentRow(scope, text, contentRows) {
	contentRows.push({
		scope,
		text,
		revisedText: ""
	});
}

function extractContentProps(entity, contentRows) {
	if (!entity) {
		return;
	}

	const id = entity.type === "checklist" ? entity.id : entity.guid;

	CONTENT_REVIEW_PROPS.forEach((prop) => {
		const value = entity[prop];
		if (typeof value === "string" && value !== "") {
			addContentRow(`${entity.type}.${id}.${prop}`, value, contentRows);
		} else if (Array.isArray(value)) {
			value.forEach((entry, index) => {
				CONTENT_REVIEW_PROPS.forEach((entryProp) => {
					const entryValue = entry[entryProp];
					if (typeof entryValue === "string" && entryValue !== "") {
						addContentRow(`${entity.type}.${id}.${prop}.[${index}]`, entryValue, contentRows);
					}
				});
			});
		}
	});
}

export function buildContentReviewContent(activity) {
	const contentRows = [];

	function recurseExtractContentProps(entity) {
		extractContentProps(entity, contentRows);

		if (entity && Array.isArray(entity.children)) {
			entity.children.forEach(recurseExtractContentProps);
		}
	}

	recurseExtractContentProps(activity);
	return { content: contentRows };
}
